import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import { createPublicRequestConfig } from "~/repository/authSession";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  ClassroomContentResponse,
  ClassroomTextInput,
  ClassroomReadOptions,
  ClassroomDetailResponse,
  ClassroomInput,
  ClassroomScheduleSlotInput,
  ClassroomScheduleSlotPatchInput,
  ClassroomScheduleSlotResponse,
  ClassroomPatchInput,
  ClassroomResponse,
  ComplementaryAssessmentActivity,
  ComplementaryAssessmentHomeworkPayload,
  ComplementaryAssessmentPreviewResponse,
  ComplementaryAssessmentResultsResponse,
  ComplementaryAssessmentUsePayload,
  ComplementaryAssessmentUseResponse,
  PublicClassroomResponse,
} from "~/types/classroom";

class ClassroomModule extends HttpFactory {
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance, "teacher");
  }

  private buildComplementaryAssessmentActionUrl(
    endpoint: string,
    contextIdKey: "lessonId" | "bookId",
    contextId: number,
    assessmentId: number,
  ): string {
    return endpoint
      .replace(`{${contextIdKey}}`, contextId.toString())
      .replace("{assessmentId}", assessmentId.toString());
  }

  private buildLessonComplementaryActivitiesUrl(lessonId: number): string {
    return ENDPOINTS.CLASSROOM.LESSON_COMPLEMENTARY_ACTIVITIES.replace(
      "{lessonId}",
      lessonId.toString(),
    );
  }

  private buildLessonComplementaryAssessmentUrl(
    lessonId: number,
    assessmentId: number,
    action?: "use",
  ): string {
    const endpoint =
      action === "use"
        ? ENDPOINTS.CLASSROOM.LESSON_COMPLEMENTARY_ASSESSMENT_USE
        : ENDPOINTS.CLASSROOM.LESSON_COMPLEMENTARY_ASSESSMENT;

    return endpoint
      .replace("{lessonId}", lessonId.toString())
      .replace("{assessmentId}", assessmentId.toString());
  }

  private buildBookComplementaryActivitiesUrl(bookId: number): string {
    return ENDPOINTS.CLASSROOM.BOOK_COMPLEMENTARY_ACTIVITIES.replace(
      "{bookId}",
      bookId.toString(),
    );
  }

  private buildBookComplementaryAssessmentUrl(
    bookId: number,
    assessmentId: number,
    action?: "use",
  ): string {
    const endpoint =
      action === "use"
        ? ENDPOINTS.CLASSROOM.BOOK_COMPLEMENTARY_ASSESSMENT_USE
        : ENDPOINTS.CLASSROOM.BOOK_COMPLEMENTARY_ASSESSMENT;

    return endpoint
      .replace("{bookId}", bookId.toString())
      .replace("{assessmentId}", assessmentId.toString());
  }

  async listClassrooms(): Promise<ClassroomResponse[]> {
    return await this.get<ClassroomResponse[]>(
      `${ENDPOINTS.CLASSROOM.CLASSROOMS}`,
    );
  }

  async getClassroom(
    id: string,
    options: ClassroomReadOptions = {},
  ): Promise<ClassroomDetailResponse> {
    return await this.get(
      `${ENDPOINTS.CLASSROOM.CLASSROOMS}${id}/`,
      options.includeTextBody ? { include_text_body: true } : {},
    );
  }

  async listLessonComplementaryActivities(
    lessonId: number,
    classroomId?: number | null,
  ): Promise<ComplementaryAssessmentActivity[]> {
    return await this.get<ComplementaryAssessmentActivity[]>(
      this.buildLessonComplementaryActivitiesUrl(lessonId),
      classroomId ? { classroom_id: classroomId } : {},
    );
  }

  async getLessonComplementaryAssessment(
    lessonId: number,
    assessmentId: number,
    classroomId?: number | null,
  ): Promise<ComplementaryAssessmentActivity> {
    return await this.get<ComplementaryAssessmentActivity>(
      this.buildLessonComplementaryAssessmentUrl(lessonId, assessmentId),
      classroomId ? { classroom_id: classroomId } : {},
    );
  }

  async useLessonComplementaryAssessment(
    lessonId: number,
    assessmentId: number,
    payload: ComplementaryAssessmentUsePayload | FormData,
  ): Promise<ComplementaryAssessmentUseResponse> {
    return await this.post<ComplementaryAssessmentUseResponse>(
      this.buildLessonComplementaryAssessmentUrl(lessonId, assessmentId, "use"),
      payload,
    );
  }

  async scheduleLessonComplementaryAssessmentHomework(
    lessonId: number,
    assessmentId: number,
    payload: ComplementaryAssessmentHomeworkPayload | FormData,
  ): Promise<ComplementaryAssessmentUseResponse> {
    return await this.post<ComplementaryAssessmentUseResponse>(
      ENDPOINTS.CLASSROOM.LESSON_COMPLEMENTARY_ASSESSMENT_HOMEWORK.replace(
        "{lessonId}",
        lessonId.toString(),
      ).replace("{assessmentId}", assessmentId.toString()),
      payload,
    );
  }

  async createLessonComplementaryAssessmentPreview(
    lessonId: number,
    assessmentId: number,
    payload: FormData,
  ): Promise<ComplementaryAssessmentPreviewResponse> {
    return await this.post<ComplementaryAssessmentPreviewResponse>(
      this.buildComplementaryAssessmentActionUrl(
        ENDPOINTS.CLASSROOM.LESSON_COMPLEMENTARY_ASSESSMENT_PREVIEW_SIMULATIONS,
        "lessonId",
        lessonId,
        assessmentId,
      ),
      payload,
    );
  }

  async deleteLessonComplementaryAssessmentPreview(
    lessonId: number,
    assessmentId: number,
    previewAssessmentId: number,
  ): Promise<void> {
    await this.delete<void>(
      `${this.buildComplementaryAssessmentActionUrl(
        ENDPOINTS.CLASSROOM.LESSON_COMPLEMENTARY_ASSESSMENT_PREVIEW_SIMULATIONS,
        "lessonId",
        lessonId,
        assessmentId,
      )}${previewAssessmentId}/`,
    );
  }

  async getLessonComplementaryAssessmentResults(
    lessonId: number,
    assessmentId: number,
  ): Promise<ComplementaryAssessmentResultsResponse> {
    return await this.get<ComplementaryAssessmentResultsResponse>(
      this.buildComplementaryAssessmentActionUrl(
        ENDPOINTS.CLASSROOM.LESSON_COMPLEMENTARY_ASSESSMENT_RESULTS,
        "lessonId",
        lessonId,
        assessmentId,
      ),
    );
  }

  async listBookComplementaryActivities(
    bookId: number,
    classroomId?: number | null,
  ): Promise<ComplementaryAssessmentActivity[]> {
    return await this.get<ComplementaryAssessmentActivity[]>(
      this.buildBookComplementaryActivitiesUrl(bookId),
      classroomId ? { classroom_id: classroomId } : {},
    );
  }

  async getBookComplementaryAssessment(
    bookId: number,
    assessmentId: number,
    classroomId?: number | null,
  ): Promise<ComplementaryAssessmentActivity> {
    return await this.get<ComplementaryAssessmentActivity>(
      this.buildBookComplementaryAssessmentUrl(bookId, assessmentId),
      classroomId ? { classroom_id: classroomId } : {},
    );
  }

  async useBookComplementaryAssessment(
    bookId: number,
    assessmentId: number,
    payload: ComplementaryAssessmentUsePayload | FormData,
  ): Promise<ComplementaryAssessmentUseResponse> {
    return await this.post<ComplementaryAssessmentUseResponse>(
      this.buildBookComplementaryAssessmentUrl(bookId, assessmentId, "use"),
      payload,
    );
  }

  async createBookComplementaryAssessmentPreview(
    bookId: number,
    assessmentId: number,
    payload: FormData,
  ): Promise<ComplementaryAssessmentPreviewResponse> {
    return await this.post<ComplementaryAssessmentPreviewResponse>(
      this.buildComplementaryAssessmentActionUrl(
        ENDPOINTS.CLASSROOM.BOOK_COMPLEMENTARY_ASSESSMENT_PREVIEW_SIMULATIONS,
        "bookId",
        bookId,
        assessmentId,
      ),
      payload,
    );
  }

  async deleteBookComplementaryAssessmentPreview(
    bookId: number,
    assessmentId: number,
    previewAssessmentId: number,
  ): Promise<void> {
    await this.delete<void>(
      `${this.buildComplementaryAssessmentActionUrl(
        ENDPOINTS.CLASSROOM.BOOK_COMPLEMENTARY_ASSESSMENT_PREVIEW_SIMULATIONS,
        "bookId",
        bookId,
        assessmentId,
      )}${previewAssessmentId}/`,
    );
  }

  async getBookComplementaryAssessmentResults(
    bookId: number,
    assessmentId: number,
  ): Promise<ComplementaryAssessmentResultsResponse> {
    return await this.get<ComplementaryAssessmentResultsResponse>(
      this.buildComplementaryAssessmentActionUrl(
        ENDPOINTS.CLASSROOM.BOOK_COMPLEMENTARY_ASSESSMENT_RESULTS,
        "bookId",
        bookId,
        assessmentId,
      ),
    );
  }

  async listScheduleSlots(): Promise<ClassroomScheduleSlotResponse[]> {
    return await this.get<ClassroomScheduleSlotResponse[]>(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS}`,
    );
  }

  async createScheduleSlot(
    input: ClassroomScheduleSlotInput,
  ): Promise<ClassroomScheduleSlotResponse> {
    return await this.post<ClassroomScheduleSlotResponse>(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS}`,
      input,
    );
  }

  async updateScheduleSlot(
    id: number,
    input: ClassroomScheduleSlotPatchInput,
  ): Promise<ClassroomScheduleSlotResponse> {
    return await this.patch<ClassroomScheduleSlotResponse>(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS}${id}/`,
      input,
    );
  }

  async deleteScheduleSlot(id: number): Promise<void> {
    return await this.delete(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS}${id}/`,
    );
  }

  async getPublicClassroom(code: string): Promise<PublicClassroomResponse> {
    return await this.get(
      `${ENDPOINTS.CLASSROOM.PUBLIC_CLASSROOMS}${code}/`,
      {},
      createPublicRequestConfig(),
    );
  }

  async getStudentClassroomByCode(
    code: string,
  ): Promise<PublicClassroomResponse> {
    return await this.get(`${ENDPOINTS.CLASSROOM.PUBLIC_CLASSROOMS}${code}/`);
  }

  async deleteClassroom(id: string): Promise<void> {
    return await this.delete(`${ENDPOINTS.CLASSROOM.CLASSROOMS}${id}`);
  }

  async createClassroom(classroom: ClassroomInput): Promise<ClassroomResponse> {
    return await this.post<ClassroomResponse>(
      `${ENDPOINTS.CLASSROOM.CLASSROOMS}`,
      classroom,
    );
  }

  async patchClassroom(
    id: number,
    classroomData: ClassroomPatchInput,
  ): Promise<ClassroomResponse> {
    return await this.patch(
      `${ENDPOINTS.CLASSROOM.CLASSROOMS}${id}/`,
      classroomData,
    );
  }

  async linkLessonToClassroom(
    classroomId: number,
    lessonId: number,
  ): Promise<void> {
    return await this.post(
      `${ENDPOINTS.CLASSROOM.LINK_LESSON_TO_CLASSROOM.replace(
        "{classroomId}",
        classroomId.toString(),
      )}`,
      {
        lessons: [lessonId],
      },
    );
  }

  async linkStudentToClassroom(
    classroomId: number,
    studentId: number,
  ): Promise<void> {
    return await this.post(
      `${ENDPOINTS.CLASSROOM.LINK_STUDENT_TO_CLASSROOM.replace(
        "{classroomId}",
        classroomId.toString(),
      )}`,
      {
        students: [studentId],
      },
    );
  }

  async unlinkStudentFromClassroom(
    classroomId: string,
    studentId: number,
  ): Promise<void> {
    return await this.post(
      `${ENDPOINTS.CLASSROOM.UNLINK_STUDENT_FROM_CLASSROOM.replace(
        "{classroomId}",
        classroomId,
      )}`,
      {
        students: [studentId],
      },
    );
  }

  async updateTeachingClassNotes(
    teachingClassId: number,
    teachersNote: string,
  ): Promise<void> {
    return await this.patch(
      `${ENDPOINTS.CLASSROOM.TEACHING_CLASSES}${teachingClassId}/`,
      {
        teacher_note: teachersNote,
      },
    );
  }

  async deleteClassroomContent(classroomContentId: number): Promise<void> {
    return await this.delete(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_CONTENT}${classroomContentId}/`,
    );
  }

  async getClassroomContent(id: number): Promise<ClassroomContentResponse> {
    return await this.get<ClassroomContentResponse>(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_CONTENT}${id}/`,
    );
  }

  async createClassroomText(
    classroomId: number,
    text: ClassroomTextInput,
  ): Promise<ClassroomContentResponse> {
    return await this.post<ClassroomContentResponse>(
      ENDPOINTS.CLASSROOM.CLASSROOM_CONTENT,
      { classroom: classroomId, content_type: "text", text },
    );
  }

  async updateClassroomText(
    id: number,
    text: ClassroomTextInput,
  ): Promise<ClassroomContentResponse> {
    return await this.patch<ClassroomContentResponse>(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_CONTENT}${id}/`,
      { text },
    );
  }

  async studentSelfLink(classroomId: number, code: string): Promise<void> {
    return await this.post(
      `${ENDPOINTS.CLASSROOM.STUDENT_SELF_LINK.replace(
        "{classroomId}",
        classroomId.toString(),
      )}`,
      { code },
    );
  }
}

export default ClassroomModule;
